import { AuthService } from '@core/service/auth.service'
import { ComponentModifier } from '@app/features/company-management/models/component-modifier.model'
import { DEFAULT_DATE_FORMAT } from '@shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.const'
import { DateTime } from 'hyperformula/typings/DateTimeHelper'
import { HyperFormula } from 'hyperformula'
import { Injectable } from '@angular/core'
import { QuestionType } from '@core/model/enums/question-type.enum'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { ScenarioCategory } from '@app/features/scope-overview/model/scenario-category.model'
import { ScenarioQuestion } from '@app/features/scope-overview/model/scenario-question.model'
import { ScopeDynamicFieldSettingModel } from '@app/features/scoping/models/scope-dynamic-field-setting.model'

export interface ScenarioDependencyGraph {
  formulaDependentsByField: Record<string, number[]>
  questionDisplayDependentsByField: Record<string, number[]>
  categoryDisplayDependentsByField: Record<string, number[]>
}

@Injectable({ providedIn: 'root' })
export class FormulaService {
  private hf: HyperFormula
  private sheetId: number
  private referencedFieldsCache = new Map<string, string[]>()
  private perfSessionDepth = 0
  private perfStats = {
    calculateFormula: { count: 0, totalMs: 0 },
    getNamedExpressionsFromFormula: { count: 0, totalMs: 0 },
    changeNamedExpression: { count: 0, totalMs: 0 },
  }
  private calculateFormulaBySource: Record<string, number> = {}

  constructor(private authService: AuthService) {
    this.hf = this.buildHyperFormula()
  }

  initialise(scenario: Scenario, dynamicFields?: ScopeDynamicFieldSettingModel[]) {
    this.referencedFieldsCache.clear()

    if (!this.hf.doesSheetExist(scenario.name)) {
      const sheetName = this.hf.addSheet(scenario.name)
      this.sheetId = this.hf.getSheetId(sheetName)
    }

    this.hf.listNamedExpressions().forEach((e) => this.hf.removeNamedExpression(e))
    this.hf.addNamedExpression('TRUE', '=TRUE()')
    this.hf.addNamedExpression('FALSE', '=FALSE()')

    scenario.categories
      .flatMap((c) => c.questions)
      .forEach((q) => {
        if (!q.fieldId) {
          return
        }
        let value = q.value ?? q.defaultValue
        if (q.type === QuestionType.FORMULA && q.formula) {
          value = `${q.formula.substring(0, 1) != '=' ? '=' : ''}${q.formula.replace('!=', '<>')}`
        } else if (typeof value == 'string') {
          value = `="${value}"`
        } else if (typeof value == 'number') {
          value = `=${value}`
        }
        if (!this.hf.isItPossibleToAddNamedExpression(q.fieldId, value)) {
          console.error(`Field ${q.fieldLabel} has an invalid field id (${q.fieldId}) and cannot be used in formulas`)
        } else {
          this.hf.addNamedExpression(q.fieldId, value)
        }
      })

    dynamicFields?.forEach((f) => {
      let value = f.value ?? f.defaultValue
      if (typeof value == 'string') {
        value = `="${value}"`
      } else if (typeof value == 'number') {
        value = `=${value}`
      }

      if (!this.hf.isItPossibleToAddNamedExpression(f.name, value)) {
        console.error(`Field ${f.name} has an invalid field id (${f.name}) and cannot be used in formulas`)
      } else {
        this.hf.addNamedExpression(f.name, value)
      }
    })
  }

  updateField(question: ScenarioQuestion) {
    if (question.fieldId) {
      let value = question.value
      if (typeof value == 'string') {
        value = `="${value}"`
      } else if (typeof value == 'number') {
        value = `=${value}`
      }
      try {
        const startedAt = this.measureStart()
        this.hf.changeNamedExpression(question.fieldId, value)
        this.measureEnd('changeNamedExpression', startedAt)
      } catch {
        return
      }
    }
  }

  updateFeeField(question: ScenarioQuestion) {
    if (question.fieldId) {
      let value = question.feeValue
      if (typeof value == 'string') {
        value = `="${value}"`
      } else if (typeof value == 'number') {
        value = `=${value}`
      }
      try {
        const startedAt = this.measureStart()
        this.hf.changeNamedExpression(question.fieldId, value)
        this.measureEnd('changeNamedExpression', startedAt)
      } catch {
        return
      }
    }
  }

  getFields() {
    return this.hf
      .listNamedExpressions()
      .filter((tag) => tag != 'TRUE' && tag != 'FALSE')
      .map((f) => {
        return { value: f, type: 'field' }
      })
  }

  getFunctions() {
    return this.hf.getAllFunctionPlugins().reduce(function (r, a) {
      return Object.assign(r, a.implementedFunctions)
    }, {})
  }

  calculate(formula: string, source = 'unknown') {
    const startedAt = this.measureStart()
    const result = this.hf.calculateFormula(this.normalizeFormula(formula), this.sheetId)
    this.measureEnd('calculateFormula', startedAt, source)
    return result
  }

  getReferencedFields(formula: string) {
    const normalizedFormula = this.normalizeFormula(formula)
    if (this.referencedFieldsCache.has(normalizedFormula)) {
      return this.referencedFieldsCache.get(normalizedFormula)
    }

    const startedAt = this.measureStart()
    const referencedFields = this.hf.getNamedExpressionsFromFormula(normalizedFormula).map((e) => e.toLowerCase())
    this.measureEnd('getNamedExpressionsFromFormula', startedAt)
    this.referencedFieldsCache.set(normalizedFormula, referencedFields)
    return referencedFields
  }

  startPerfInteraction(label: string) {
    if (!this.isPerfEnabled()) return

    if (this.perfSessionDepth === 0) {
      this.perfStats = {
        calculateFormula: { count: 0, totalMs: 0 },
        getNamedExpressionsFromFormula: { count: 0, totalMs: 0 },
        changeNamedExpression: { count: 0, totalMs: 0 },
      }
      this.calculateFormulaBySource = {}
      console.time(label)
    }
    this.perfSessionDepth++
  }

  endPerfInteraction(label: string) {
    if (!this.isPerfEnabled() || this.perfSessionDepth === 0) return

    this.perfSessionDepth--
    if (this.perfSessionDepth !== 0) return

    console.timeEnd(label)
    const totalMs =
      this.perfStats.calculateFormula.totalMs +
      this.perfStats.getNamedExpressionsFromFormula.totalMs +
      this.perfStats.changeNamedExpression.totalMs

    const rows = Object.entries(this.perfStats).map(([operation, stats]) => ({
      operation,
      count: stats.count,
      totalMs: Number(stats.totalMs.toFixed(1)),
      avgMs: stats.count ? Number((stats.totalMs / stats.count).toFixed(2)) : 0,
      shareOfTrackedHF: totalMs ? `${((stats.totalMs / totalMs) * 100).toFixed(1)}%` : '0.0%',
    }))

    console.groupCollapsed(`[HF PERF] ${label}`)
    console.table(rows)
    if (Object.keys(this.calculateFormulaBySource).length) {
      const bySourceRows = Object.entries(this.calculateFormulaBySource)
        .sort(([, countA], [, countB]) => countB - countA)
        .map(([source, count]) => ({ source, count }))
      console.table(bySourceRows)
    }
    console.groupEnd()
  }

  buildScenarioDependencyGraph(scenario: Scenario): ScenarioDependencyGraph {
    const formulaDependentsByField: Record<string, number[]> = {}
    const questionDisplayDependentsByField: Record<string, number[]> = {}
    const categoryDisplayDependentsByField: Record<string, number[]> = {}

    const addUnique = (target: Record<string, number[]>, field: string, id: number) => {
      const key = field.toLowerCase()
      if (!target[key]) {
        target[key] = [id]
        return
      }

      if (!target[key].includes(id)) {
        target[key].push(id)
      }
    }

    scenario.categories.forEach((category) => {
      if (category.displayCondition) {
        this.getReferencedFields(category.displayCondition).forEach((fieldId) =>
          addUnique(categoryDisplayDependentsByField, fieldId, category.id)
        )
      }

      category.questions.forEach((question) => {
        if (question.formula) {
          this.getReferencedFields(question.formula).forEach((fieldId) =>
            addUnique(formulaDependentsByField, fieldId, question.id)
          )
        }

        if (question.displayCondition) {
          this.getReferencedFields(question.displayCondition).forEach((fieldId) =>
            addUnique(questionDisplayDependentsByField, fieldId, question.id)
          )
        }
      })
    })

    return { formulaDependentsByField, questionDisplayDependentsByField, categoryDisplayDependentsByField }
  }

  getReferencedFieldsForQuestion(fieldId: string, scenario: Scenario, modifiers: ComponentModifier[]) {
    const questionDependencies = scenario.categories
      .flatMap((c) =>
        c.questions.map((q) => {
          return { ...q, categoryName: c.name }
        })
      )
      .filter(
        (q) =>
          (q.formula && this.getReferencedFields(q.formula).includes(fieldId.toLowerCase())) ||
          (q.displayCondition && this.getReferencedFields(q.displayCondition).includes(fieldId.toLowerCase()))
      )
    const modifierDependencies = modifiers.filter(
      (m) => m.formula && this.getReferencedFields(m.formula).includes(fieldId.toLowerCase())
    )
    return { questionDependencies, modifierDependencies }
  }

  getReferencedFieldsForCategory(category: ScenarioCategory, scenario: Scenario, modifiers: ComponentModifier[]) {
    const fieldIds = category.questions.flatMap((q) => q.fieldId?.toLowerCase())
    const questionDependencies = scenario.categories
      .filter((c) => c.id != category.id)
      .flatMap((c) =>
        c.questions.map((q) => {
          return { ...q, categoryName: c.name }
        })
      )
      .filter(
        (q) =>
          (q.formula && this.getReferencedFields(q.formula).some((f) => fieldIds.includes(f))) ||
          (q.displayCondition && this.getReferencedFields(q.displayCondition).some((f) => fieldIds.includes(f)))
      )
    const modifierDependencies = modifiers.filter(
      (m) => m.formula && this.getReferencedFields(m.formula).some((f) => fieldIds.includes(f))
    )
    return { questionDependencies, modifierDependencies }
  }

  isItPossibleToAddFieldId(fieldId: string) {
    return this.hf.isItPossibleToAddNamedExpression(fieldId, null)
  }

  private buildHyperFormula(): HyperFormula {
    const companyDateFormat = this.authService.loggedInUser?.company?.companyDateFormat

    return HyperFormula.buildEmpty({
      licenseKey: 'gpl-v3',
      dateFormats: [companyDateFormat || DEFAULT_DATE_FORMAT],
      parseDateTime: (dateTimeString: string): DateTime => {
        if (!dateTimeString) return undefined
        const date = new Date(dateTimeString)
        return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
      },
    })
  }

  private normalizeFormula(formula: string): string {
    return `${formula.substring(0, 1) != '=' ? '=' : ''}${formula.replace(/!=/g, '<>')}`
  }

  private isPerfEnabled(): boolean {
    if (typeof window === 'undefined') return false

    const hasQueryFlag = new URLSearchParams(window.location.search).get('hfPerf') === '1'
    const hasLocalStorageFlag = window.localStorage?.getItem('scopePerfHF') === '1'
    return hasQueryFlag || hasLocalStorageFlag
  }

  private measureStart(): number {
    if (!this.isPerfEnabled() || this.perfSessionDepth === 0) return 0
    return performance.now()
  }

  private measureEnd(operation: keyof FormulaService['perfStats'], startedAt: number, source?: string) {
    if (!startedAt || !this.isPerfEnabled() || this.perfSessionDepth === 0) return

    const duration = performance.now() - startedAt
    this.perfStats[operation].count++
    this.perfStats[operation].totalMs += duration

    if (operation === 'calculateFormula') {
      const sourceKey = source || 'unknown'
      this.calculateFormulaBySource[sourceKey] = (this.calculateFormulaBySource[sourceKey] || 0) + 1
    }
  }
}
